<template>
    <table>
        <thead>
            <tr>
                <th>{{ $t('validation.form.parametersTable.pkParameters') }}</th>
                <th class="text-right">{{ $t('validation.form.parametersTable.typicalPatient') }}</th>
                <th class="text-right">{{ $t('validation.form.parametersTable.aPriori') }}</th>
                <th class="text-right">{{ $t('validation.form.parametersTable.aPosteriori') }}</th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="mapEntry in parametersVM.parameters" :key="mapEntry[1].id">
                <td>{{ mapEntry[1].id }}</td>
                <td class="text-right">{{ mapEntry[1].population.toFixed(3) }}</td>
                <td class="text-right">{{ mapEntry[1].aPriori?.toFixed(3) ?? '-' }}</td>
                <td class="text-right">{{ mapEntry[1].aPosteriori?.toFixed(3) ?? '-' }}</td>
            </tr>
        </tbody>
    </table>
</template>

<script lang="ts">
import { ComputingOption } from '@/service/models/request/computation/computing-option';
import { PredictionTraits } from '@/service/models/request/computation/computing-traits/prediction-traits';
import { Request } from '@/service/models/request/computation/request';
import { ComputationResult } from '@/service/models/response/computation-result';
import { DataPrediction } from '@/service/models/response/computation/data-responses/data-prediction';
import { ParametersVM } from '@/service/view-models/cycle-data/parameters-vm';
import { useRequestDataStore } from '@/store/request-data'
import { DatesRange } from '@/utilities/types/dates-range';
import { Duration } from '@/utilities/types/duration';
import { defineComponent } from 'vue'

export default defineComponent({
    emits: {
        "pkParametersLoaded": (payload: ParametersVM) => typeof payload === "object",
    },

    async setup(props, {emit})
    {
        const requestData = useRequestDataStore();

        if (requestData.adjustmentDate === null) {
            throw new Error("No adjustment date is defined");
        }

        // Pk parameters should be retrieved at the moment of the adjustment. Here we don't need any curve
        // so wi will just query for a single second of data after the adjustment date.
        const adjustmentMoment = new DatesRange(
            requestData.adjustmentDate,
            Duration.fromSeconds(1).addTo(requestData.adjustmentDate)
        );

        /**
         * A helper function to build a PredictionTraits object for for requests sent by this component.
         */
        function makePredictionTraits(paramType: ComputingOption.ParametersType): PredictionTraits
        {
            return new PredictionTraits(
                adjustmentMoment,
                0,
                new ComputingOption(paramType, ComputingOption.CompartmentOption.ALL_ACTIVE_MOIETIES, false, true, false)
            );
        }

        // Make a bunch of requests to retrieve Pk parameters
        let aPrioriRequest: Request|null = null;
        let aPosterioriRequest: Request|null = null;
        const populationRequest = requestData.makeComputationRequest(makePredictionTraits(ComputingOption.ParametersType.POPULATION));

        if (requestData.hasCovariates) {
            aPrioriRequest = requestData.makeComputationRequest(makePredictionTraits(ComputingOption.ParametersType.APRIORI));
        }

        if (requestData.hasSamples) {
            aPosterioriRequest = requestData.makeComputationRequest(makePredictionTraits(ComputingOption.ParametersType.APOSTERIORI));
        }

        const query = requestData.makeComputationQuery([populationRequest, aPrioriRequest, aPosterioriRequest], true);
        const result = await requestData.makeApiRequest(query).load() as ComputationResult;

        // Flip the received parameters into a map, keyed by parameter id
        const populationResponse = result.getResponseById(populationRequest.requestId);
        const aPrioriResponse = result.getResponseByIdOrNull(aPrioriRequest?.requestId ?? "");
        const aPosterioriResponse = result.getResponseByIdOrNull(aPosterioriRequest?.requestId ?? "");

        if (!(populationResponse.data instanceof DataPrediction)) {
            throw Error("Population response received from API is not a <dataPrediction>");
        }

        const parametersVM = new ParametersVM(populationResponse.data.getFirstCycleParameters());

        if (aPrioriResponse !== null && aPrioriResponse.data instanceof DataPrediction) {
            parametersVM.fillAPrioriParams(aPrioriResponse.data.getFirstCycleParameters());
        }

        if (aPosterioriResponse !== null && aPosterioriResponse.data instanceof DataPrediction) {
            parametersVM.fillAPosterioriParams(aPosterioriResponse.data.getFirstCycleParameters());
        }

        emit("pkParametersLoaded", parametersVM);

        return {
            parametersVM,
        }
    },
})
</script>

